import axios from "axios";
import { plainToInstance } from "class-transformer";
import { DocumentRetrievalFailureReason, DocumentRetrievalTaskBatchDto, DocumentRetrievalTaskDownloadDto, PatientTEFCAAuthInfoDto, SuggestedFastenEndpointDto, } from "healthex-common/document.retrieval.dtos";
import { err } from "healthex-common/result";
import { asEnum } from "healthex-common/utils";
import { authenticatedApiConfig, generateURL, retryTooManyRequests } from "./BaseApiService";
export class DocumentRetrievalService {
    constructor(authenticationProvider, environment) {
        this.authenticationProvider = authenticationProvider;
        this.environment = environment;
    }
    async getExistingRetrieval(patientId, input) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents", { patientId }), { ...input, documentSelectionComplete: false }, await authenticatedApiConfig(this.authenticationProvider));
        return response.data ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data) : null;
    }
    async retrievePatientData(patientId, input) {
        return await retryTooManyRequests(async () => {
            try {
                const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/retrieve", {
                    patientId,
                }), input, await authenticatedApiConfig(this.authenticationProvider));
                return response.data
                    ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data)
                    : err(DocumentRetrievalFailureReason.GENERIC_ERROR);
            }
            catch (e) {
                return this.checkThrownError(e);
            }
        });
    }
    async getTaskBatch(patientId, batchId) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents", { patientId }), { batchId }, await authenticatedApiConfig(this.authenticationProvider));
        return response.data ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data) : null;
    }
    async getTaskBatchFinalized(patientId, batchId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/:batchId/finalized", { patientId, batchId }), await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateTaskBatch(patientId, batchId, input) {
        const response = await axios.put(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}`, input, await authenticatedApiConfig(this.authenticationProvider));
        return response.data ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data) : null;
    }
    async getTaskBatchDownloadLink(patientId, batchId) {
        const response = await axios.get(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}/download`, await authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(DocumentRetrievalTaskDownloadDto, response.data);
    }
    async getMostRecentDownloadableOrProcessingTaskBatch(patientId) {
        const response = await axios.get(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/most-recent-downloadable-or-processing`, await authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(DocumentRetrievalTaskBatchDto, response.data);
    }
    async getMostRecentFinalizedTaskBatch(patientId, mustHaveRecords) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/most-recent-finalized", {
            patientId,
        }), await authenticatedApiConfig(this.authenticationProvider, { mustHaveRecords }));
        return response.data ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data) : null;
    }
    async skipTEFCARetrieval(patientId, batchId) {
        try {
            const response = await axios.post(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}/tefca/skip`, {}, await authenticatedApiConfig(this.authenticationProvider));
            return response.data
                ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data)
                : err(DocumentRetrievalFailureReason.GENERIC_ERROR);
        }
        catch (e) {
            return this.checkThrownError(e);
        }
    }
    async startTEFCARetrieval(patientId, batchId) {
        try {
            const response = await axios.post(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}/tefca-retrieval`, {}, await authenticatedApiConfig(this.authenticationProvider));
            return response.data
                ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data)
                : err(DocumentRetrievalFailureReason.GENERIC_ERROR);
        }
        catch (e) {
            return this.checkThrownError(e);
        }
    }
    checkThrownError(e) {
        var _a;
        if (axios.isAxiosError(e)) {
            if ((_a = e.response) === null || _a === void 0 ? void 0 : _a.data) {
                const errorData = e.response.data;
                if (errorData.message) {
                    const failureReason = asEnum(DocumentRetrievalFailureReason)(errorData.message);
                    if (failureReason)
                        return err(failureReason);
                }
            }
        }
        throw e;
    }
    async getFacilitatedFHIRAuthUrl(patientId, batchId, fhirEndpoint, redirectUrl, authType, epicIntegration) {
        var _a;
        const input = {
            fhirEndpoint,
            redirectUrl,
            authType,
            epicIntegration,
        };
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/:batchId/facilitated-fhir/authorize-url", {
            patientId,
            batchId,
        }), input, await authenticatedApiConfig(this.authenticationProvider));
        return (_a = response.data) !== null && _a !== void 0 ? _a : null;
    }
    async getFacilitatedFHIRRecords(patientId, batchId, locationId, fhirEndpoint, authType, redirectUri, code, epicIntegration, createFromEpicDirectory) {
        const input = {
            locationId,
            fhirEndpoint,
            redirectUri,
            authType,
            code,
            epicIntegration,
            createFromEpicDirectory,
        };
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/:batchId/facilitated-fhir/records", {
            patientId,
            batchId,
        }), input, await authenticatedApiConfig(this.authenticationProvider));
        return response.data ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data) : null;
    }
    async trackFastenRetrieval(patientId, batchId, connectionId, portalId, brandId, endpointId, source) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}/fasten`, { connectionId, portalId, brandId, endpointId, source }, await authenticatedApiConfig(this.authenticationProvider));
        return response.data ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data) : null;
    }
    async getSuggestedFastenEndpoints(patientId, batchId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/:batchId/suggested-fasten-endpoints", {
            patientId,
            batchId,
        }), await authenticatedApiConfig(this.authenticationProvider));
        return Array.isArray(response.data) ? response.data.map((item) => plainToInstance(SuggestedFastenEndpointDto, item)) : [];
    }
    async getTaskBatchDownloadPdfLink(patientId, batchId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/:batchId/download-pdf", {
            patientId,
            batchId,
        }), await authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(DocumentRetrievalTaskDownloadDto, response.data);
    }
    async createDocumentDelivery(patientId, input) {
        await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/document-deliveries", { patientId }), input, await authenticatedApiConfig(this.authenticationProvider));
    }
    async getPatientTEFCAAuthInfo(patientId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/tefca-auth-info", { patientId }), await authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(PatientTEFCAAuthInfoDto, response.data);
    }
}
